/**
 * TFIC Level-Based Styling System
 * Provides CSS classes and style objects for level-based visual identity
 */

export interface LevelStyle {
  frameClass: string;
  accentColor: string;
  glowColor: string;
  badgeColor: string;
}

/**
 * Get CSS class for profile frame based on level
 */
export function getProfileFrameClass(level: number): string {
  if (level === 0 || level === 1) return '';
  if (level === 2) return 'profile-frame-operator';
  if (level === 3) return 'profile-frame-specialist';
  if (level === 4) return 'profile-frame-veteran';
  if (level === 5) return 'profile-frame-elite';
  if (level === 6) return 'profile-frame-master';
  if (level === 7) return 'profile-frame-commander';
  if (level === 8) return 'profile-frame-ace';
  if (level === 9) return 'profile-frame-legend';
  if (level >= 10) return 'profile-frame-paragon';
  return '';
}

/**
 * Get level-based styling configuration
 */
export function getLevelStyle(level: number): LevelStyle {
  const styles: Record<number, LevelStyle> = {
    0: {
      frameClass: '',
      accentColor: '#6b7280',
      glowColor: 'rgba(107, 114, 128, 0.3)',
      badgeColor: '#6b7280'
    },
    1: {
      frameClass: '',
      accentColor: '#3b82f6',
      glowColor: 'rgba(59, 130, 246, 0.3)',
      badgeColor: '#3b82f6'
    },
    2: {
      frameClass: 'profile-frame-operator',
      accentColor: '#14b8a6',
      glowColor: 'rgba(20, 184, 166, 0.4)',
      badgeColor: '#14b8a6'
    },
    3: {
      frameClass: 'profile-frame-specialist',
      accentColor: '#a855f7',
      glowColor: 'rgba(168, 85, 247, 0.4)',
      badgeColor: '#a855f7'
    },
    4: {
      frameClass: 'profile-frame-veteran',
      accentColor: '#cd7f32',
      glowColor: 'rgba(205, 127, 50, 0.5)',
      badgeColor: '#cd7f32'
    },
    5: {
      frameClass: 'profile-frame-elite',
      accentColor: '#fbbf24',
      glowColor: 'rgba(251, 191, 36, 0.6)',
      badgeColor: '#fbbf24'
    },
    6: {
      frameClass: 'profile-frame-master',
      accentColor: '#d1d5db',
      glowColor: 'rgba(209, 213, 219, 0.6)',
      badgeColor: '#d1d5db'
    },
    7: {
      frameClass: 'profile-frame-commander',
      accentColor: '#dc2626',
      glowColor: 'rgba(220, 38, 38, 0.6)',
      badgeColor: '#dc2626'
    },
    8: {
      frameClass: 'profile-frame-ace',
      accentColor: '#f97316',
      glowColor: 'rgba(249, 115, 22, 0.7)',
      badgeColor: '#f97316'
    },
    9: {
      frameClass: 'profile-frame-legend',
      accentColor: '#7c3aed',
      glowColor: 'rgba(124, 58, 237, 0.8)',
      badgeColor: '#7c3aed'
    },
    10: {
      frameClass: 'profile-frame-paragon',
      accentColor: '#ffffff',
      glowColor: 'rgba(255, 255, 255, 0.6)',
      badgeColor: 'linear-gradient(135deg, #ef4444, #f97316, #fbbf24, #22c55e, #3b82f6, #a855f7)'
    }
  };

  return styles[level] || styles[0];
}

/**
 * Check if level qualifies for enhanced profile features
 */
export function hasMottoUnlocked(level: number): boolean {
  return level >= 3;
}

export function hasEliteLogsUnlocked(level: number): boolean {
  return level >= 5;
}

export function hasCustomThemeUnlocked(level: number): boolean {
  return level >= 6;
}

export function hasServiceProfileUnlocked(level: number): boolean {
  return level >= 10;
}

/**
 * Get level tier for general categorization
 */
export function getLevelTier(level: number): 'recruit' | 'standard' | 'veteran' | 'elite' | 'legendary' | 'paragon' {
  if (level === 0) return 'recruit';
  if (level <= 2) return 'standard';
  if (level <= 4) return 'veteran';
  if (level <= 7) return 'elite';
  if (level <= 9) return 'legendary';
  return 'paragon';
}

/**
 * Check if member should be featured on recognition pages
 */
export function isVeteranShowcase(level: number): boolean {
  return level >= 4;
}

export function isMasterContributor(level: number): boolean {
  return level >= 6;
}

export function isLegendBoard(level: number): boolean {
  return level >= 9;
}

export function isParagonHall(level: number): boolean {
  return level >= 10;
}
